import PropTypes from 'prop-types';
import React, { useContext, useRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

// components
import StepperNavMobile from '@molecules/StepperNavMobile';
import AccountInformationForm from '@organisms/AccountInformationForm';
import { BodyContainerStyled } from '@styled';
import TrackPageView from '@tracking/TrackPageView';
import ErrorHandler from '../../../_molecules/ErrorHandler';
import FooterNavigation from '../../../_molecules/FooterNavigation';
import SectionBreadcrumbs from '../../../_molecules/SectionBreadcrumbs';
import SectionBreadcrumb from '../../../_molecules/SectionBreadcrumb';

// Functionality
import { LoadingContext } from '@context/LoadingProvider';
import { addEventRegistrationToCart } from '@general/helpers/cart.helpers';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useEventFromAPI from '@hooks/apiLegacy/useEventFromAPI';
import { useScreenMinMd, useScreenSmDown } from '@hooks/useResponsive';
import useViewer from '@hooks/useViewer';
import { getMemberInfoBreadcrumbLink } from '@general/helpers/member.helpers';

const BreadCrumbContainerStyled = styled.div`
    background-color: #f9f9f9;
`;

const EventRegistrationMemberInfoPage = ({ isNew }) => {
    const { setIsLoading } = useContext(LoadingContext);
    const history = useHistory();
    const formRef = useRef();
    const match = useRouteMatch();
    const eventCode = match.params?.code;
    const { viewer } = useViewer();
    const { event } = useEventFromAPI({ code: eventCode });
    const { cart, refetch } = useCartFromAPI({ viewerId: viewer?.recordId });
    const isScreenMinMd = useScreenMinMd();
    const isScreenSmDown = useScreenSmDown();
    // For event registration, the only time company fields should be enabled is when:
    //   - the user is a prospect (i.e. they just signed up and haven't purchased a membership)
    //   - the user doesn't have a company (until they complete checkout, they will only have a deferred company)
    const disableCompanySelection = !(viewer?.prospect && !viewer?.company && !isNew);

    if (!cart) return null;

    return (
        <ErrorHandler>
            <TrackPageView>
                {isScreenMinMd && (
                    <BreadCrumbContainerStyled>
                        <div className="container">
                            <div className="py-1rem">
                                <SectionBreadcrumbs className="mt-2">
                                    <SectionBreadcrumb isActive>Account Info</SectionBreadcrumb>
                                    <SectionBreadcrumb route="/cart/view">Cart Details</SectionBreadcrumb>
                                    <SectionBreadcrumb route="/cart/payment">Payment Info</SectionBreadcrumb>
                                </SectionBreadcrumbs>
                            </div>
                        </div>
                    </BreadCrumbContainerStyled>
                )}

                {isScreenSmDown && (
                    <StepperNavMobile
                        activeIndex={0}
                        steps={[
                            { label: 'Account Info', href: getMemberInfoBreadcrumbLink(cart, viewer?.recordId) },
                            { label: 'Cart Details', href: '/cart/view' },
                            { label: 'Payment Info', href: '/cart/payment' },
                        ]}
                    />
                )}

                <BodyContainerStyled className="container">
                    <div className="gap-8 grid-cols-8 md:grid">
                        <div className="col-span-5">
                            <AccountInformationForm
                                disableCompanySelection={disableCompanySelection}
                                formRef={formRef}
                                memberId={isNew ? null : viewer?.recordId}
                                onMemberSaved={async member => {
                                    await addEventRegistrationToCart({
                                        cart,
                                        event,
                                        memberId: member.id,
                                        viewerId: viewer.recordId,
                                        useSpeedBump: viewer.prospect ? viewer.prospect : !!viewer.company,
                                    });

                                    await refetch();

                                    setIsLoading(false);
                                    history.push('/cart/view');
                                }}
                                viewerId={viewer?.recordId}
                            />
                        </div>
                    </div>
                </BodyContainerStyled>

                <FooterNavigation
                    nextLabel="Save & Continue"
                    previousLabel="Cancel"
                    onNextClick={() => {
                        if (formRef.current) {
                            formRef.current.handleSubmit();
                        }
                    }}
                    onPreviousClick={() => history.push(`/event-registration/${eventCode}/start`)}
                />
            </TrackPageView>
        </ErrorHandler>
    );
};

EventRegistrationMemberInfoPage.propTypes = {
    isNew: PropTypes.bool.isRequired,
};

export default EventRegistrationMemberInfoPage;
